import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Nav, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import SideNavbar from "../../components/common/SideNavbar";
import TopNavbar from "../../components/common/TopNavbar";
import GenericCrudPanel from "../../components/common/crud/GenericCrudPanel";
import DetailGrid from "../../components/common/DetailGrid";

const HR_BASE = `${process.env.REACT_APP_BACKEND_API_URL}/hr`;

const ASSET_STATUS_OPTIONS = [
  { value: "available", label: "Available" },
  { value: "assigned", label: "Assigned" },
  { value: "repair", label: "Repair" },
  { value: "maintenance", label: "Maintenance" },
  { value: "reserved", label: "Reserved" },
  { value: "lost", label: "Lost" },
  { value: "scrapped", label: "Scrapped" },
];

const LICENSE_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
  { value: "inactive", label: "Inactive" },
];

const ASSET_DETAIL_FIELDS = [
  { name: "asset_code", label: "Asset Code", required: true },
  { name: "asset_type", label: "Asset Type", required: true, type: "select", optionsKey: "assetTypeOptions" },
  { name: "asset_name", label: "Asset Name", required: true },
  { name: "status", label: "Status", required: true, type: "select", optionsKey: "assetStatusOptions" },
  { name: "assigned_to", label: "Assigned To", type: "select", optionsKey: "employeeOptions" },
  { name: "condition", label: "Condition" },
  { name: "serial_number", label: "Serial Number" },
  { name: "brand", label: "Brand" },
  { name: "model", label: "Model" },
  { name: "purchase_date", label: "Purchase Date", type: "date" },
  { name: "purchase_cost", label: "Purchase Cost", type: "number", step: "0.01" },
  { name: "warranty_expiry", label: "Warranty Expiry", type: "date" },
  { name: "vendor", label: "Vendor" },
  { name: "remarks", label: "Remarks", type: "textarea" },
];

const LAPTOP_FIELDS = [
  { name: "processor", label: "Processor", required: true },
  { name: "ram_gb", label: "RAM (GB)", required: true, type: "number" },
  { name: "storage_gb", label: "Storage (GB)", required: true, type: "number" },
  { name: "operating_system", label: "Operating System", required: true },
  { name: "hostname", label: "Hostname" },
  { name: "mac_address", label: "MAC Address" },
  { name: "windows_version", label: "Windows Version" },
  { name: "bitlocker_enabled", label: "BitLocker Enabled", type: "checkbox" },
  { name: "bitlocker_key", label: "BitLocker Key" },
  { name: "antivirus", label: "Antivirus" },
];

const ASSET_STATUS_BADGE_CLASS = {
  available: "bg-success",
  assigned: "bg-info text-dark",
  repair: "bg-warning text-dark",
  maintenance: "bg-warning text-dark",
  reserved: "bg-secondary",
  lost: "bg-danger",
  scrapped: "bg-dark",
};

function AssetStatusBadge({ status }) {
  return <span className={`badge ${ASSET_STATUS_BADGE_CLASS[status] || "bg-secondary"}`}>{status || "-"}</span>;
}

function isLaptopType(assetRecord) {
  const typeName = assetRecord?.asset_type_data?.name || "";
  return typeName.trim().toLowerCase() === "laptop";
}

const belongsToAsset = (assetId) => (row) => String(row.asset_data?.id ?? row.asset) === String(assetId);

function AssetDetail() {
  const { assetId } = useParams();
  const navigate = useNavigate();

  const [menuDrawerShow, setMenuDrawerShow] = useState(true);
  const [dataFromTopNavBar, setDataFromTopNavBar] = useState("");
  const [activeTab, setActiveTab] = useState("details");

  const [loading, setLoading] = useState(true);
  const [asset, setAsset] = useState(null);
  const [laptop, setLaptop] = useState(null);
  const [options, setOptions] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);

  const [showEditCanvas, setShowEditCanvas] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [targetStatus, setTargetStatus] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);

  const [timelineData, setTimelineData] = useState(null);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [openSection, setOpenSection] = useState("assignments");

  const [showLaptopCanvas, setShowLaptopCanvas] = useState(false);
  const [laptopForm, setLaptopForm] = useState({});
  const [laptopSaving, setLaptopSaving] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnRecord, setReturnRecord] = useState(null);
  const [returnForm, setReturnForm] = useState({ returned_on: "", condition_returned: "", remarks: "" });
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeRecord, setCompleteRecord] = useState(null);
  const [completeForm, setCompleteForm] = useState({ completed_on: "", status: "" });
  const reloadRef = useRef(() => {});

  const handleDataFromTopNavBar = (permissiondata) => {
    setDataFromTopNavBar(permissiondata);
  };

  const loadLaptop = async (assetRecordId) => {
    try {
      const response = await axios.get(`${HR_BASE}/laptop/`);
      const found = (response.data || []).find((item) => String(item.asset_data?.id ?? item.asset) === String(assetRecordId));
      setLaptop(found || null);
    } catch (err) {
      // Non-critical; leave laptop unset.
    }
  };

  const loadRecentActivity = async (assetRecordId) => {
    try {
      const [assignmentsRes, maintenanceRes, licensesRes, documentsRes] = await Promise.all([
        axios.get(`${HR_BASE}/assetassignment/`),
        axios.get(`${HR_BASE}/assetmaintenance/`),
        axios.get(`${HR_BASE}/softwarelicense/`),
        axios.get(`${HR_BASE}/assetdocument/`),
      ]);
      const matches = belongsToAsset(assetRecordId);

      const items = [];
      const latestAssignment = (assignmentsRes.data || []).filter(matches).sort((a, b) => (b.assigned_on || "").localeCompare(a.assigned_on || ""))[0];
      if (latestAssignment) {
        items.push({ label: `Assigned to ${latestAssignment.employee_data?.employee_code || latestAssignment.employee_data?.user_data?.name || "-"}`, date: latestAssignment.assigned_on || "" });
      }

      const latestMaintenance = (maintenanceRes.data || []).filter(matches).sort((a, b) => (b.started_on || "").localeCompare(a.started_on || ""))[0];
      if (latestMaintenance) {
        items.push({ label: `Maintenance - ${latestMaintenance.status || "-"}`, date: latestMaintenance.started_on || "" });
      }

      const latestLicense = (licensesRes.data || []).filter(matches).sort((a, b) => (b.purchase_date || "").localeCompare(a.purchase_date || ""))[0];
      if (latestLicense) {
        items.push({ label: `License added - ${latestLicense.software_name || "-"}`, date: latestLicense.purchase_date || "" });
      }

      const latestDocument = (documentsRes.data || []).filter(matches).sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""))[0];
      if (latestDocument) {
        items.push({ label: `Document uploaded - ${latestDocument.document_type || "-"}`, date: latestDocument.uploaded_at || "" });
      }

      items.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setRecentActivity(items);
    } catch (err) {
      // Non-critical panel; fail silently.
    }
  };

  const loadAsset = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${HR_BASE}/asset/${assetId}/`);
      setAsset(response.data);
      loadRecentActivity(response.data.id);
      if (isLaptopType(response.data)) {
        loadLaptop(response.data.id);
      }
    } catch (err) {
      setAsset(null);
      toast.error("Error loading asset.");
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [assetTypes, employees] = await Promise.all([
        axios.get(`${HR_BASE}/assettype/`),
        axios.get(`${HR_BASE}/employee/`),
      ]);
      setOptions({
        assetTypeOptions: (assetTypes.data || []).map((item) => ({ value: item.id, label: item.name || item.id })),
        employeeOptions: (employees.data || []).map((item) => ({ value: item.id, label: `${item.employee_code} - ${item.user_data?.name || ""}` })),
        assetStatusOptions: ASSET_STATUS_OPTIONS,
      });
    } catch (err) {
      toast.error("Error loading form options.");
    }
  };

  useEffect(() => {
    loadAsset();
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  const tabs = useMemo(() => {
    const list = [{ key: "details", label: "Details" }];
    if (isLaptopType(asset)) {
      list.push({ key: "laptop", label: "Laptop Details" });
    }
    list.push(
      { key: "softwarelicense", label: "Software Licenses" },
      { key: "assetassignment", label: "Assignments" },
      { key: "assetmaintenance", label: "Maintenance" },
      { key: "assetdocument", label: "Documents" },
    );
    return list;
  }, [asset]);

  const openEditCanvas = () => {
    const prepared = {};
    ASSET_DETAIL_FIELDS.forEach((field) => {
      const value = asset[field.name];
      if (typeof value === "object" && value !== null && value.id) {
        prepared[field.name] = value.id;
      } else if (value === null || value === undefined) {
        prepared[field.name] = "";
      } else {
        prepared[field.name] = value;
      }
    });
    setEditForm(prepared);
    setShowEditCanvas(true);
  };

  const renderEditField = (field) => {
    if (field.type === "select") {
      return (
        <Form.Select
          value={editForm[field.name] ?? ""}
          onChange={(event) => setEditForm((prev) => ({ ...prev, [field.name]: event.target.value }))}
        >
          <option value="">Select {field.label}</option>
          {(options[field.optionsKey] || []).map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </Form.Select>
      );
    }
    if (field.type === "textarea") {
      return (
        <Form.Control
          as="textarea"
          rows={3}
          value={editForm[field.name] ?? ""}
          onChange={(event) => setEditForm((prev) => ({ ...prev, [field.name]: event.target.value }))}
        />
      );
    }
    return (
      <Form.Control
        type={field.type || "text"}
        step={field.step}
        value={editForm[field.name] ?? ""}
        onChange={(event) => setEditForm((prev) => ({ ...prev, [field.name]: event.target.value }))}
      />
    );
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    const missing = ASSET_DETAIL_FIELDS.filter((field) => field.required && !editForm[field.name]);
    if (missing.length > 0) {
      toast.error(`Please fill: ${missing.map((field) => field.label).join(", ")}`);
      return;
    }

    const payload = { ...editForm };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "") delete payload[key];
    });

    setEditSaving(true);
    try {
      const response = await axios.put(`${HR_BASE}/asset/${asset.id}/`, payload);
      toast.success("Asset updated successfully.");
      setAsset(response.data);
      setShowEditCanvas(false);
      if (isLaptopType(response.data)) loadLaptop(response.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.detail || "Error updating asset.");
    } finally {
      setEditSaving(false);
    }
  };

  const openStatusModal = () => {
    setTargetStatus(asset.status || "");
    setShowStatusModal(true);
  };

  const toggleSection = (section) => {
    setOpenSection((prev) => (prev === section ? "" : section));
  };

  const handleLoadAssetTimeline = async () => {
    try {
      setTimelineLoading(true);
      const response = await axios.get(`${HR_BASE}/asset/${asset.id}/timeline/`);
      setTimelineData(response.data);
      setOpenSection("assignments");
      setShowTimelineModal(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error fetching asset timeline.");
    } finally {
      setTimelineLoading(false);
    }
  };

  const submitStatusUpdate = async () => {
    if (!targetStatus) {
      toast.error("Status is required.");
      return;
    }
    setStatusSaving(true);
    try {
      const response = await axios.put(`${HR_BASE}/asset/${asset.id}/status/`, { status: targetStatus });
      toast.success("Asset status updated successfully.");
      setShowStatusModal(false);
      setAsset((prev) => ({ ...prev, status: response.data?.status || targetStatus }));
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error updating asset status.");
    } finally {
      setStatusSaving(false);
    }
  };

  const openLaptopCanvas = () => {
    if (laptop) {
      const prepared = {};
      LAPTOP_FIELDS.forEach((field) => {
        const value = laptop[field.name];
        prepared[field.name] = value === null || value === undefined ? (field.type === "checkbox" ? false : "") : value;
      });
      setLaptopForm(prepared);
    } else {
      setLaptopForm(LAPTOP_FIELDS.reduce((acc, field) => {
        acc[field.name] = field.type === "checkbox" ? false : "";
        return acc;
      }, {}));
    }
    setShowLaptopCanvas(true);
  };

  const renderLaptopField = (field) => {
    if (field.type === "checkbox") {
      return (
        <Form.Check
          type="checkbox"
          checked={Boolean(laptopForm[field.name])}
          onChange={(event) => setLaptopForm((prev) => ({ ...prev, [field.name]: event.target.checked }))}
          label={field.label}
        />
      );
    }
    return (
      <Form.Control
        type={field.type || "text"}
        value={laptopForm[field.name] ?? ""}
        onChange={(event) => setLaptopForm((prev) => ({ ...prev, [field.name]: event.target.value }))}
      />
    );
  };

  const saveLaptop = async (event) => {
    event.preventDefault();
    const missing = LAPTOP_FIELDS.filter((field) => field.required && !laptopForm[field.name]);
    if (missing.length > 0) {
      toast.error(`Please fill: ${missing.map((field) => field.label).join(", ")}`);
      return;
    }

    const payload = { ...laptopForm, asset: asset.id };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "") delete payload[key];
    });

    setLaptopSaving(true);
    try {
      const response = laptop
        ? await axios.put(`${HR_BASE}/laptop/${laptop.id}/`, payload)
        : await axios.post(`${HR_BASE}/laptop/`, payload);
      toast.success(laptop ? "Laptop details updated successfully." : "Laptop details created successfully.");
      setLaptop(response.data);
      setShowLaptopCanvas(false);
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.detail || "Error saving laptop details.");
    } finally {
      setLaptopSaving(false);
    }
  };

  const softwareLicenseModule = useMemo(() => ({
    key: `softwarelicense-${asset?.id || "none"}`,
    title: "Software Licenses",
    endpoint: "/softwarelicense/",
    fields: [
      { name: "asset", label: "Asset", required: true, type: "hidden" },
      { name: "software_name", label: "Software Name", required: true },
      { name: "license_key", label: "License Key" },
      { name: "assigned_email", label: "Assigned Email", type: "email" },
      { name: "version", label: "Version" },
      { name: "status", label: "Status", required: true, type: "select", optionsKey: "licenseStatusOptions" },
      { name: "purchase_date", label: "Purchase Date", type: "date" },
      { name: "expiry_date", label: "Expiry Date", type: "date" },
      { name: "renewal_cost", label: "Renewal Cost", type: "number", step: "0.01" },
    ],
    columns: [
      { name: "Software", selector: (row) => row.software_name || "-", sortable: true },
      { name: "Status", selector: (row) => row.status || "-", sortable: true },
      { name: "Expiry", selector: (row) => row.expiry_date || "-", sortable: true },
    ],
    initialForm: { asset: asset?.id || "", software_name: "", license_key: "", assigned_email: "", version: "", status: "active", purchase_date: "", expiry_date: "", renewal_cost: "" },
  }), [asset?.id]);

  const assetAssignmentModule = useMemo(() => ({
    key: `assetassignment-${asset?.id || "none"}`,
    title: "Assignments",
    endpoint: "/assetassignment/",
    fields: [
      { name: "asset", label: "Asset", required: true, type: "hidden" },
      { name: "employee", label: "Employee", required: true, type: "select", optionsKey: "employeeOptions" },
      { name: "assigned_on", label: "Assigned On", required: true, type: "date" },
      { name: "returned_on", label: "Returned On", type: "date" },
      { name: "condition_given", label: "Condition Given", required: true },
      { name: "condition_returned", label: "Condition Returned" },
      { name: "remarks", label: "Remarks", type: "textarea" },
    ],
    columns: [
      { name: "Employee", selector: (row) => row.employee_data?.employee_code || row.employee_data?.user_data?.name || "-", sortable: true },
      { name: "Assigned On", selector: (row) => row.assigned_on || "-", sortable: true },
      { name: "Returned On", selector: (row) => row.returned_on || "-", sortable: true },
    ],
    initialForm: { asset: asset?.id || "", employee: "", assigned_on: "", returned_on: "", condition_given: "", condition_returned: "", remarks: "" },
  }), [asset?.id]);

  const assetMaintenanceModule = useMemo(() => ({
    key: `assetmaintenance-${asset?.id || "none"}`,
    title: "Maintenance",
    endpoint: "/assetmaintenance/",
    fields: [
      { name: "asset", label: "Asset", required: true, type: "hidden" },
      { name: "issue", label: "Issue", required: true, type: "textarea" },
      { name: "vendor", label: "Vendor", required: true },
      { name: "repair_cost", label: "Repair Cost", type: "number", step: "0.01" },
      { name: "started_on", label: "Started On", required: true, type: "date" },
      { name: "completed_on", label: "Completed On", type: "date" },
      { name: "status", label: "Status", required: true },
    ],
    columns: [
      { name: "Vendor", selector: (row) => row.vendor || "-", sortable: true },
      { name: "Status", selector: (row) => row.status || "-", sortable: true },
      { name: "Started", selector: (row) => row.started_on || "-", sortable: true },
      { name: "Completed", selector: (row) => row.completed_on || "-", sortable: true },
    ],
    initialForm: { asset: asset?.id || "", issue: "", vendor: "", repair_cost: "", started_on: "", completed_on: "", status: "" },
  }), [asset?.id]);

  const assetDocumentModule = useMemo(() => ({
    key: `assetdocument-${asset?.id || "none"}`,
    title: "Documents",
    endpoint: "/assetdocument/",
    fields: [
      { name: "asset", label: "Asset", required: true, type: "hidden" },
      { name: "document_type", label: "Document Type", required: true },
      { name: "file", label: "File", type: "file" },
    ],
    columns: [
      { name: "Document Type", selector: (row) => row.document_type || "-", sortable: true },
      { name: "Uploaded At", selector: (row) => row.uploaded_at || "-", sortable: true },
    ],
    initialForm: { asset: asset?.id || "", document_type: "", file: null },
  }), [asset?.id]);

  const loadLicenseOptions = async () => ({ licenseStatusOptions: LICENSE_STATUS_OPTIONS });
  const loadAssignmentOptions = async () => {
    const employees = await axios.get(`${HR_BASE}/employee/`);
    return { employeeOptions: (employees.data || []).map((item) => ({ value: item.id, label: `${item.employee_code} - ${item.user_data?.name || ""}` })) };
  };
  const loadMaintenanceOptions = async () => ({});
  const loadDocumentOptions = async () => ({});

  const openReturnModal = (row, reload) => {
    reloadRef.current = reload;
    setReturnRecord(row);
    setReturnForm({ returned_on: "", condition_returned: "", remarks: row.remarks || "" });
    setShowReturnModal(true);
  };

  const submitReturn = async () => {
    if (!returnRecord?.id) return;
    if (!returnForm.returned_on || !returnForm.condition_returned) {
      toast.error("returned_on and condition_returned are required.");
      return;
    }
    try {
      setActionLoading(true);
      await axios.put(`${HR_BASE}/assetassignment/${returnRecord.id}/return/`, returnForm);
      toast.success("Asset returned successfully.");
      setShowReturnModal(false);
      reloadRef.current();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error returning asset.");
    } finally {
      setActionLoading(false);
    }
  };

  const openCompleteModal = (row, reload) => {
    reloadRef.current = reload;
    setCompleteRecord(row);
    setCompleteForm({ completed_on: "", status: row.status || "" });
    setShowCompleteModal(true);
  };

  const submitComplete = async () => {
    if (!completeRecord?.id) return;
    try {
      setActionLoading(true);
      await axios.put(`${HR_BASE}/assetmaintenance/${completeRecord.id}/complete/`, completeForm);
      toast.success("Maintenance completed successfully.");
      setShowCompleteModal(false);
      reloadRef.current();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error completing maintenance.");
    } finally {
      setActionLoading(false);
    }
  };

  const renderAssignmentRowActions = (row, module, reload) => (
    !row.returned_on ? (
      <Button className="btn btn-sm btn-success border-0 mx-1" title="Mark Returned" onClick={() => openReturnModal(row, reload)}>
        <i className="fa fa-undo"></i>
      </Button>
    ) : null
  );

  const renderMaintenanceRowActions = (row, module, reload) => (
    !row.completed_on ? (
      <Button className="btn btn-sm btn-success border-0 mx-1" title="Complete" onClick={() => openCompleteModal(row, reload)}>
        <i className="fa fa-check"></i>
      </Button>
    ) : null
  );

  if (loading) {
    return (
      <div className="page-content-bg">
        <TopNavbar sidebarCollapsed={menuDrawerShow} topNavbarStatic={true} sendDataToParent={handleDataFromTopNavBar} />
        <SideNavbar sidebarCollapsed={menuDrawerShow} activeMenu="HR" activeSubMenu="AssetManagement" activeSubMenuLevel1="" permissionFromTopNavbar={dataFromTopNavBar} />
        <div className={menuDrawerShow ? "row main-content-collapsed text-center py-5" : "row main-content text-center py-5"}>
          <Spinner className="mx-auto text-inox-green" as="span" animation="border" size="lg" role="status" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="page-content-bg">
        <TopNavbar sidebarCollapsed={menuDrawerShow} topNavbarStatic={true} sendDataToParent={handleDataFromTopNavBar} />
        <SideNavbar sidebarCollapsed={menuDrawerShow} activeMenu="HR" activeSubMenu="AssetManagement" activeSubMenuLevel1="" permissionFromTopNavbar={dataFromTopNavBar} />
        <div className={menuDrawerShow ? "row main-content-collapsed text-center py-5" : "row main-content text-center py-5"}>
          <p>Asset not found.</p>
        </div>
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="page-content-bg">
      <TopNavbar sidebarCollapsed={menuDrawerShow} topNavbarStatic={true} sendDataToParent={handleDataFromTopNavBar} />
      <SideNavbar
        sidebarCollapsed={menuDrawerShow}
        activeMenu="HR"
        activeSubMenu="AssetManagement"
        activeSubMenuLevel1=""
        permissionFromTopNavbar={dataFromTopNavBar}
      />

      <div className={menuDrawerShow ? "row main-content-collapsed text-left py-0" : "row main-content text-left py-0"}>
        <div className="col-12 page-header">
          <div className="row">
            <div className="col-sm-8 py-1">
              <Button className="btn btn-sm btn-inox-green border-0 mx-2" onClick={() => setMenuDrawerShow(!menuDrawerShow)}>
                {menuDrawerShow ? <span><i className="fa fa-chevron-left mx-1"></i></span> : <span><i className="fa fa-chevron-right mx-1"></i></span>}
              </Button>
              <span className="page-header-text">
                <span className="pointer" onClick={() => navigate("/hr/asset")}>HR / Assets</span> / {asset.asset_code}
              </span>
            </div>
          </div>
        </div>

        <div className="col-12 text-left" style={{ marginTop: -5, paddingLeft: 20, paddingRight: 30 }}>
          <Button variant="link" className="px-0 mb-3 text-inox-green" onClick={() => navigate("/hr/asset")}>
            <i className="fa fa-arrow-left me-2"></i>Back to Assets
          </Button>

          <div className="border rounded p-4 mb-3 bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap">
              <div>
                <h5 className="mb-1">{asset.asset_name}</h5>
                <div className="text-muted">
                  {asset.asset_code} &bull; {asset.asset_type_data?.name || "No type set"} &bull; Assigned to: {asset.assigned_to_data?.employee_code || asset.assigned_to_data?.user_data?.name || "Unassigned"}
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <AssetStatusBadge status={asset.status} />
                <Button className="btn btn-sm btn-info border-0" onClick={handleLoadAssetTimeline}>
                  {timelineLoading ? <Spinner as="span" animation="border" size="sm" /> : <i className="fa fa-history me-1"></i>}Timeline
                </Button>
                <Button className="btn btn-sm btn-warning border-0" onClick={openStatusModal}>
                  <i className="fa fa-refresh me-1"></i>Update Status
                </Button>
              </div>
            </div>

            <Nav variant="tabs" className="mt-4 mb-3">
              {tabs.map((tab) => (
                <Nav.Item key={tab.key}>
                  <Nav.Link className="text-inox-green" active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)}>
                    {tab.label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>

            {activeTab === "details" ? (
              <div>
                <div className="d-flex justify-content-end mb-2">
                  <Button className="btn btn-sm btn-inox-green border-0" onClick={openEditCanvas}>
                    <i className="fa fa-edit me-2"></i>Edit Details
                  </Button>
                </div>
                <DetailGrid
                  sections={[
                    {
                      title: "Asset Information",
                      fields: [
                        { label: "Asset Code", value: asset.asset_code, icon: "fa-barcode", mono: true },
                        { label: "Asset Type", value: asset.asset_type_data?.name, icon: "fa-tag" },
                        { label: "Status", value: <AssetStatusBadge status={asset.status} />, icon: "fa-circle" },
                        { label: "Assigned To", value: asset.assigned_to_data?.employee_code || asset.assigned_to_data?.user_data?.name, icon: "fa-user" },
                        { label: "Condition", value: asset.condition, icon: "fa-heartbeat" },
                        { label: "Serial Number", value: asset.serial_number, icon: "fa-hashtag", mono: true },
                      ],
                    },
                    {
                      title: "Purchase & Vendor",
                      fields: [
                        { label: "Brand", value: asset.brand, icon: "fa-industry" },
                        { label: "Model", value: asset.model, icon: "fa-cube" },
                        { label: "Purchase Date", value: asset.purchase_date, icon: "fa-calendar" },
                        { label: "Purchase Cost", value: asset.purchase_cost === null || asset.purchase_cost === undefined || asset.purchase_cost === "" ? "" : Number(asset.purchase_cost).toLocaleString(), icon: "fa-money" },
                        { label: "Warranty Expiry", value: asset.warranty_expiry, icon: "fa-shield" },
                        { label: "Vendor", value: asset.vendor, icon: "fa-truck" },
                      ],
                    },
                    {
                      title: "Notes",
                      fields: [
                        { label: "Remarks", value: asset.remarks, icon: "fa-sticky-note", wide: true },
                      ],
                    },
                  ]}
                />
              </div>
            ) : null}

            {activeTab === "laptop" ? (
              <div>
                <div className="d-flex justify-content-end mb-2">
                  <Button className="btn btn-sm btn-inox-green border-0" onClick={openLaptopCanvas}>
                    <i className="fa fa-edit me-2"></i>
                    {laptop ? "Edit Laptop Details" : "Set Up Laptop Details"}
                  </Button>
                </div>
                {laptop ? (
                  <DetailGrid
                    sections={[
                      {
                        title: "Hardware",
                        fields: [
                          { label: "Processor", value: laptop.processor, icon: "fa-cogs" },
                          { label: "RAM", value: laptop.ram_gb ? `${laptop.ram_gb} GB` : "", icon: "fa-tachometer" },
                          { label: "Storage", value: laptop.storage_gb ? `${laptop.storage_gb} GB` : "", icon: "fa-hdd-o" },
                          { label: "Operating System", value: laptop.operating_system, icon: "fa-desktop" },
                          { label: "Windows Version", value: laptop.windows_version, icon: "fa-windows" },
                          { label: "Hostname", value: laptop.hostname, icon: "fa-server", mono: true },
                          { label: "MAC Address", value: laptop.mac_address, icon: "fa-wifi", mono: true },
                        ],
                      },
                      {
                        title: "Security & Management",
                        fields: [
                          { label: "BitLocker Enabled", value: laptop.bitlocker_enabled ? "Yes" : "No", icon: "fa-lock" },
                          { label: "Antivirus", value: laptop.antivirus, icon: "fa-shield" },
                          { label: "BitLocker Key", value: laptop.bitlocker_key, icon: "fa-key", mono: true, wide: true },
                        ],
                      },
                    ]}
                  />
                ) : (
                  <p className="text-muted py-4 text-center">No laptop details set up yet for this asset. Click "Set Up Laptop Details" to add them.</p>
                )}
              </div>
            ) : null}

            {activeTab === "softwarelicense" ? (
              <GenericCrudPanel module={softwareLicenseModule} baseUrl={HR_BASE} loadOptions={loadLicenseOptions} filterRows={(rows) => rows.filter(belongsToAsset(asset.id))} />
            ) : null}

            {activeTab === "assetassignment" ? (
              <GenericCrudPanel
                module={assetAssignmentModule}
                baseUrl={HR_BASE}
                loadOptions={loadAssignmentOptions}
                extraRowActions={renderAssignmentRowActions}
                filterRows={(rows) => rows.filter(belongsToAsset(asset.id))}
              />
            ) : null}

            {activeTab === "assetmaintenance" ? (
              <GenericCrudPanel
                module={assetMaintenanceModule}
                baseUrl={HR_BASE}
                loadOptions={loadMaintenanceOptions}
                extraRowActions={renderMaintenanceRowActions}
                filterRows={(rows) => rows.filter(belongsToAsset(asset.id))}
              />
            ) : null}

            {activeTab === "assetdocument" ? (
              <GenericCrudPanel module={assetDocumentModule} baseUrl={HR_BASE} loadOptions={loadDocumentOptions} filterRows={(rows) => rows.filter(belongsToAsset(asset.id))} />
            ) : null}
          </div>

          <div className="row mb-4">
            <div className="col-md-7 mb-3">
              <div className="border rounded p-3 bg-white h-100">
                <h6 className="mb-3">Recent Activity</h6>
                {recentActivity.length === 0 ? (
                  <p className="text-muted mb-0">No recent activity.</p>
                ) : (
                  recentActivity.map((item, index) => (
                    <div key={index} className={index < recentActivity.length - 1 ? "d-flex justify-content-between border-bottom py-2" : "d-flex justify-content-between py-2"}>
                      <span>{item.label}</span>
                      <span className="text-muted">{item.date}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            {/* <div className="col-md-5 mb-3">
              <div className="border rounded p-3 bg-white h-100">
                <h6 className="mb-3">Quick Links</h6>
                <div className="d-flex flex-column gap-2">
                  {isLaptopType(asset) ? (
                    <Button variant="link" className="text-inox-green text-start px-0" onClick={() => setActiveTab("laptop")}>View Laptop Details &rarr;</Button>
                  ) : null}
                  <Button variant="link" className="text-inox-green text-start px-0" onClick={() => setActiveTab("softwarelicense")}>View Software Licenses &rarr;</Button>
                  <Button variant="link" className="text-inox-green text-start px-0" onClick={() => setActiveTab("assetassignment")}>View Assignments &rarr;</Button>
                  <Button variant="link" className="text-inox-green text-start px-0" onClick={() => setActiveTab("assetmaintenance")}>View Maintenance &rarr;</Button>
                  <Button variant="link" className="text-inox-green text-start px-0" onClick={() => setActiveTab("assetdocument")}>View Documents &rarr;</Button>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      <div className={`offcanvas-backdrop ${showEditCanvas ? "show" : ""}`} style={{ display: showEditCanvas ? "block" : "none" }} />
      <div
        className={`offcanvas offcanvas-bottom ${showEditCanvas ? "show" : ""}`}
        tabIndex="-1"
        style={{ visibility: showEditCanvas ? "visible" : "hidden", height: "60%", overflowY: "auto" }}
      >
        <div className="offcanvas-header border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
          <h5 className="offcanvas-title mb-0">Edit Asset Details</h5>
          <Button className="btn btn-danger btn-sm" size="sm" onClick={() => setShowEditCanvas(false)}>
            <i className="fa fa-times text-white"></i>
          </Button>
        </div>
        <div className="offcanvas-body px-4 py-3 text-left">
          <Form onSubmit={saveEdit}>
            <div className="row">
              {ASSET_DETAIL_FIELDS.map((field) => (
                <div className="col-md-6 mb-3" key={field.name}>
                  <Form.Label>{field.label}{field.required ? <span className="text-danger"> *</span> : null}</Form.Label>
                  {renderEditField(field)}
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button variant="secondary" onClick={() => setShowEditCanvas(false)}>Close</Button>
              <Button type="submit" className="btn-inox-green border-0">
                {editSaving ? <Spinner as="span" animation="border" size="sm" /> : "Save"}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <div className={`offcanvas-backdrop ${showLaptopCanvas ? "show" : ""}`} style={{ display: showLaptopCanvas ? "block" : "none" }} />
      <div
        className={`offcanvas offcanvas-bottom ${showLaptopCanvas ? "show" : ""}`}
        tabIndex="-1"
        style={{ visibility: showLaptopCanvas ? "visible" : "hidden", height: "60%", overflowY: "auto" }}
      >
        <div className="offcanvas-header border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
          <h5 className="offcanvas-title mb-0">{laptop ? "Edit Laptop Details" : "Set Up Laptop Details"}</h5>
          <Button className="btn btn-danger btn-sm" size="sm" onClick={() => setShowLaptopCanvas(false)}>
            <i className="fa fa-times text-white"></i>
          </Button>
        </div>
        <div className="offcanvas-body px-4 py-3 text-left">
          <Form onSubmit={saveLaptop}>
            <div className="row">
              {LAPTOP_FIELDS.map((field) => (
                <div className="col-md-6 mb-3" key={field.name}>
                  <Form.Label>{field.label}{field.required ? <span className="text-danger"> *</span> : null}</Form.Label>
                  {renderLaptopField(field)}
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button variant="secondary" onClick={() => setShowLaptopCanvas(false)}>Close</Button>
              <Button type="submit" className="btn-inox-green border-0">
                {laptopSaving ? <Spinner as="span" animation="border" size="sm" /> : "Save"}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <div className="modal fade show" style={{ display: showTimelineModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Asset Timeline</h5>
              <button type="button" className="btn-close" onClick={() => setShowTimelineModal(false)}></button>
            </div>
            <div className="modal-body">
              <h4><strong>Asset:</strong> {timelineData?.asset?.asset_code} - {timelineData?.asset?.asset_name}</h4>
              <div className="accordion" id="assetTimelineAccordion">

                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      className={`accordion-button ${openSection !== "assignments" ? "collapsed" : ""}`}
                      type="button"
                      onClick={() => toggleSection("assignments")}
                    >
                      Assignments ({timelineData?.assignments?.length || 0})
                    </button>
                  </h2>
                  <div className={`accordion-collapse collapse ${openSection === "assignments" ? "show" : ""}`}>
                    <div className="accordion-body">
                      {timelineData?.assignments?.length ? (
                        timelineData.assignments.map((assignment) => (
                          <div className="border rounded p-3 mb-3" key={assignment.id}>
                            <p><strong>Assigned to:</strong> {assignment.employee__name}</p>
                            <p><strong>Assigned on:</strong> {assignment.assigned_on}</p>
                            <p><strong>Returned on:</strong> {assignment.returned_on || "-"}</p>
                            <p><strong>Condition returned:</strong> {assignment.condition_returned || "-"}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted mb-0">No assignments found.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      className={`accordion-button ${openSection !== "maintenance" ? "collapsed" : ""}`}
                      type="button"
                      onClick={() => toggleSection("maintenance")}
                    >
                      Maintenance ({timelineData?.maintenance?.length || 0})
                    </button>
                  </h2>
                  <div className={`accordion-collapse collapse ${openSection === "maintenance" ? "show" : ""}`}>
                    <div className="accordion-body">
                      {timelineData?.maintenance?.length ? (
                        timelineData.maintenance.map((item) => (
                          <div className="border rounded p-3 mb-3" key={item.id}>
                            <p><strong>Vendor:</strong> {item.vendor}</p>
                            <p><strong>Issue:</strong> {item.issue}</p>
                            <p><strong>Repair Cost:</strong> {item.repair_cost}</p>
                            <p><strong>Started On:</strong> {item.started_on}</p>
                            <p><strong>Completed On:</strong> {item.completed_on || "-"}</p>
                            <p><strong>Status:</strong> {item.status}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted mb-0">No maintenance history.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      className={`accordion-button ${openSection !== "licenses" ? "collapsed" : ""}`}
                      type="button"
                      onClick={() => toggleSection("licenses")}
                    >
                      Licenses ({timelineData?.licenses?.length || 0})
                    </button>
                  </h2>
                  <div className={`accordion-collapse collapse ${openSection === "licenses" ? "show" : ""}`}>
                    <div className="accordion-body">
                      {timelineData?.licenses?.length ? (
                        timelineData.licenses.map((license) => (
                          <div className="border rounded p-3 mb-3" key={license.id}>
                            <p><strong>Software Name:</strong> {license.software_name}</p>
                            <p><strong>License Key:</strong> {license.license_key}</p>
                            <p><strong>Assigned Email:</strong> {license.assigned_email}</p>
                            <p><strong>Expiry Date:</strong> {license.expiry_date}</p>
                            <p><strong>Status:</strong> {license.status}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted mb-0">No licenses found.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      className={`accordion-button ${openSection !== "documents" ? "collapsed" : ""}`}
                      type="button"
                      onClick={() => toggleSection("documents")}
                    >
                      Documents ({timelineData?.documents?.length || 0})
                    </button>
                  </h2>
                  <div className={`accordion-collapse collapse ${openSection === "documents" ? "show" : ""}`}>
                    <div className="accordion-body">
                      {timelineData?.documents?.length ? (
                        timelineData.documents.map((document) => (
                          <div className="border rounded p-3 mb-3" key={document.id}>
                            <p>
                              <strong>Document Type:</strong>{" "}
                              {document.file ? (
                                <a href={document.file} target="_blank" rel="noopener noreferrer">
                                  {document.document_type}
                                </a>
                              ) : (
                                <span>{document.document_type}</span>
                              )}
                            </p>
                            <p><strong>Uploaded On:</strong> {document.uploaded_at}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted mb-0">No documents found.</p>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade show" style={{ display: showStatusModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Update Asset Status</h5>
              <button type="button" className="btn-close" onClick={() => setShowStatusModal(false)}></button>
            </div>
            <div className="modal-body">
              <Form.Select value={targetStatus} onChange={(e) => setTargetStatus(e.target.value)}>
                <option value="">Select Status</option>
                {ASSET_STATUS_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </Form.Select>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowStatusModal(false)}>Close</Button>
              <Button className="btn-inox-green border-0" onClick={submitStatusUpdate}>
                {statusSaving ? <Spinner as="span" animation="border" size="sm" /> : "Update"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade show" style={{ display: showReturnModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Mark Assignment Returned</h5>
              <button type="button" className="btn-close" onClick={() => setShowReturnModal(false)}></button>
            </div>
            <div className="modal-body">
              <Form.Label>Returned On *</Form.Label>
              <Form.Control type="date" value={returnForm.returned_on} onChange={(e) => setReturnForm((prev) => ({ ...prev, returned_on: e.target.value }))} />
              <Form.Label className="mt-2">Condition Returned *</Form.Label>
              <Form.Control value={returnForm.condition_returned} onChange={(e) => setReturnForm((prev) => ({ ...prev, condition_returned: e.target.value }))} />
              <Form.Label className="mt-2">Remarks</Form.Label>
              <Form.Control as="textarea" rows={3} value={returnForm.remarks} onChange={(e) => setReturnForm((prev) => ({ ...prev, remarks: e.target.value }))} />
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowReturnModal(false)}>Close</Button>
              <Button className="btn-inox-green border-0" onClick={submitReturn}>
                {actionLoading ? <Spinner as="span" animation="border" size="sm" /> : "Submit"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade show" style={{ display: showCompleteModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Complete Maintenance</h5>
              <button type="button" className="btn-close" onClick={() => setShowCompleteModal(false)}></button>
            </div>
            <div className="modal-body">
              <Form.Label>Completed On</Form.Label>
              <Form.Control type="date" value={completeForm.completed_on} onChange={(e) => setCompleteForm((prev) => ({ ...prev, completed_on: e.target.value }))} />
              <Form.Label className="mt-2">Status</Form.Label>
              <Form.Control value={completeForm.status} onChange={(e) => setCompleteForm((prev) => ({ ...prev, status: e.target.value }))} />
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowCompleteModal(false)}>Close</Button>
              <Button className="btn-inox-green border-0" onClick={submitComplete}>
                {actionLoading ? <Spinner as="span" animation="border" size="sm" /> : "Submit"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}

export default AssetDetail;
